import { useEffect, useState } from "react";
import { useOutletContext, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Package, Users, Truck, ClipboardList, Flag, TrendingUp, PoundSterling, Repeat, Mail } from "lucide-react";

const gbp = (n) => "£" + (n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const shortMonth = (yyyyMm) => {
  const [y, m] = yyyyMm.split("-");
  const d = new Date(Number(y), Number(m) - 1, 1);
  return d.toLocaleDateString("en-GB", { month: "short" });
};

const KIND_LABEL = {
  verified: "Verified badge",
  listing_featured: "Featured listing",
  brief_featured: "Featured request",
};

export default function AdminOverview() {
  const { stats } = useOutletContext();
  const [ana, setAna] = useState(null);
  const [sendingDigest, setSendingDigest] = useState(false);

  useEffect(() => {
    api.get("/admin/analytics/overview").then(({ data }) => setAna(data)).catch(() => setAna(null));
  }, []);

  const sendDigestNow = async () => {
    if (!window.confirm("Send the Business Digest to your admin email right now?")) return;
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/analytics/send-digest");
      toast.success(`Digest sent to ${data.sent_to}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed — check SMTP settings");
    } finally { setSendingDigest(false); }
  };

  const cards = [
    { key: "reports_open", label: "Open reports", to: "/admin/reports", icon: Flag, urgent: true },
    { key: "verifications_pending", label: "Verifications pending", to: "/admin/verifications", icon: AlertTriangle, urgent: true },
    { key: "listings_pending", label: "Listings pending review", to: "/admin/listings?status=pending", icon: Package, urgent: true },
    { key: "listings_flagged", label: "Flagged listings", to: "/admin/listings?status=flagged", icon: AlertTriangle, urgent: true },
    { key: "companies_pending", label: "Companies awaiting approval", to: "/admin/companies?approved=false", icon: Truck },
    { key: "users", label: "Total users", to: "/admin/users", icon: Users },
    { key: "verified_users", label: "Verified users", to: "/admin/users", icon: Users },
    { key: "briefs_open", label: "Open requests", to: "/admin/briefs?status=open", icon: ClipboardList },
  ];

  const maxMonth = Math.max(1, ...(ana?.revenue_by_month?.map((m) => m.total_gbp) || [1]));

  return (
    <div>
      <div className="overline text-safety">Overview</div>
      <div className="mt-1 flex items-center justify-between flex-wrap gap-3">
        <h1 className="font-display font-black text-3xl tracking-tighter text-forest">Site moderation snapshot</h1>
        <Button onClick={sendDigestNow} disabled={sendingDigest} variant="outline" data-testid="send-digest-btn"
          className="rounded-none h-10 border-forest text-forest hover:bg-forest hover:text-white">
          <Mail className="w-4 h-4 mr-2" /> {sendingDigest ? "Sending…" : "Send digest now"}
        </Button>
      </div>

      {/* Revenue KPIs */}
      <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="admin-revenue-kpis">
        <div className="border border-border bg-forest text-white p-5">
          <div className="flex items-center justify-between">
            <PoundSterling className="w-4 h-4 text-safety" />
            <span className="overline text-[9px] text-safety">rolling 30d</span>
          </div>
          <div className="mt-4 font-display font-black text-3xl tabular-nums" data-testid="kpi-gross-30d">
            {ana ? gbp(ana.gross_last_30d_gbp) : "—"}
          </div>
          <div className="mt-1 overline text-white/70 text-[10px]">Gross revenue · last 30 days</div>
        </div>
        <div className="border border-border bg-white p-5">
          <div className="flex items-center justify-between">
            <Repeat className="w-4 h-4 text-forest" />
            <span className="overline text-[9px] text-forest">live</span>
          </div>
          <div className="mt-4 font-display font-black text-3xl text-forest tabular-nums" data-testid="kpi-active-subs">
            {ana?.active_subs_count ?? "—"}
          </div>
          <div className="mt-1 overline text-muted-foreground text-[10px]">Active subscriptions</div>
        </div>
        <div className="border border-border bg-white p-5">
          <div className="flex items-center justify-between">
            <TrendingUp className="w-4 h-4 text-forest" />
            <span className="overline text-[9px] text-muted-foreground">lifetime</span>
          </div>
          <div className="mt-4 font-display font-black text-3xl text-forest tabular-nums" data-testid="kpi-gross-lifetime">
            {ana ? gbp(ana.gross_lifetime_gbp) : "—"}
          </div>
          <div className="mt-1 overline text-muted-foreground text-[10px]">Gross revenue · all-time</div>
        </div>
      </div>

      {/* Charts row */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[1.4fr,1fr] gap-6">
        {/* Monthly revenue bars */}
        <div className="border border-border bg-white p-5" data-testid="admin-revenue-chart">
          <div className="flex items-center justify-between">
            <div>
              <div className="overline text-safety text-[10px]">Revenue by month</div>
              <div className="font-display text-lg text-forest">Last 12 months</div>
            </div>
            <div className="overline text-[9px] text-muted-foreground">£ inc. VAT</div>
          </div>
          {ana?.revenue_by_month?.length ? (
            <div className="mt-5 flex items-end gap-2 h-40">
              {ana.revenue_by_month.map((m) => (
                <div key={m.month} className="flex-1 flex flex-col items-center justify-end gap-2" title={`${m.month} · ${gbp(m.total_gbp)}`}>
                  <div
                    className="w-full bg-forest transition-all hover:bg-safety"
                    style={{ height: `${Math.max(2, (m.total_gbp / maxMonth) * 100)}%` }}
                  />
                  <div className="overline text-[9px] text-muted-foreground">{shortMonth(m.month)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="mt-6 py-10 text-center text-sm text-muted-foreground">No paid transactions yet.</div>
          )}
        </div>

        {/* Revenue by kind (last 30d) */}
        <div className="border border-border bg-white p-5" data-testid="admin-revenue-by-kind">
          <div className="overline text-safety text-[10px]">30-day revenue by product</div>
          <div className="font-display text-lg text-forest">Where the money came from</div>
          {ana?.revenue_by_kind?.length ? (
            <div className="mt-5 space-y-3">
              {ana.revenue_by_kind.map((k) => {
                const total30 = ana.gross_last_30d_gbp || 1;
                const pct = Math.round((k.total_gbp / total30) * 100);
                return (
                  <div key={k.kind}>
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-foreground">{KIND_LABEL[k.kind] || k.kind}</span>
                      <span className="tabular-nums font-mono">{gbp(k.total_gbp)}</span>
                    </div>
                    <div className="mt-1 h-2 bg-sage-light">
                      <div className="h-full bg-forest" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="mt-6 py-10 text-center text-sm text-muted-foreground">No paid transactions in the last 30 days.</div>
          )}
        </div>
      </div>

      {/* Top posters */}
      <div className="mt-6 border border-border bg-white p-5" data-testid="admin-top-posters">
        <div className="flex items-center justify-between">
          <div>
            <div className="overline text-safety text-[10px]">Engagement</div>
            <div className="font-display text-lg text-forest">Top clearance-request posters</div>
          </div>
          <Link to="/admin/briefs" className="overline text-[10px] text-forest hover:underline">All requests →</Link>
        </div>
        {ana?.top_posters?.length ? (
          <div className="mt-4 divide-y divide-border">
            {ana.top_posters.map((p, i) => (
              <div key={p.user_id || i} className="py-3 grid grid-cols-[24px,1fr,auto] items-center gap-3" data-testid={`top-poster-${i}`}>
                <div className="w-6 h-6 border border-forest text-forest flex items-center justify-center overline text-[10px] font-bold">{i + 1}</div>
                <div>
                  <div className="font-display text-sm text-forest truncate">{p.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{p.email || "—"}</div>
                </div>
                <div className="overline text-xs text-safety tabular-nums">{p.brief_count} req.</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-6 py-8 text-center text-sm text-muted-foreground">No clearance requests posted yet.</div>
        )}
      </div>

      {/* Existing moderation cards */}
      <div className="mt-10 overline text-safety text-[10px]">Moderation queues</div>
      <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3">
        {cards.map((c) => {
          const val = stats?.[c.key] ?? 0;
          return (
            <Link
              key={c.key}
              to={c.to}
              data-testid={`admin-stat-${c.key}`}
              className={`border border-border bg-white p-5 block hover:-translate-y-0.5 hover:shadow-lg transition-transform duration-200 ${c.urgent && val > 0 ? "border-safety" : ""}`}
            >
              <div className="flex items-center justify-between">
                <c.icon className={`w-4 h-4 ${c.urgent && val > 0 ? "text-safety" : "text-forest"}`} />
                {c.urgent && val > 0 && <span className="w-2 h-2 bg-safety" />}
              </div>
              <div className="mt-4 font-display font-black text-3xl text-forest tabular-nums">{val}</div>
              <div className="mt-1 overline text-muted-foreground text-[10px] leading-tight">{c.label}</div>
            </Link>
          );
        })}
      </div>

      <div className="mt-10 border border-border bg-sage-light p-6">
        <div className="overline text-safety">Moderator playbook</div>
        <ol className="mt-4 space-y-2 text-sm text-foreground/90 leading-relaxed">
          <li><b>1. Reports</b> — every user report needs a physical admin decision. Never auto-resolve.</li>
          <li><b>2. Listings pending review</b> — approve or flag before they appear in the marketplace.</li>
          <li><b>3. Suspended users</b> — cannot log in, sessions killed instantly.</li>
        </ol>
      </div>
    </div>
  );
}
